import { z } from 'zod'
import { countryEnum } from './country-enum'
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  unique,
} from 'drizzle-orm/pg-core'
import { createdAt, id, updatedAt } from '../utils/columns'
import { relations } from 'drizzle-orm'
import { currencyEnum } from '../shared/currency-enum'
import { createInsertSchema } from 'drizzle-zod'
import type { EntityOrganization } from '../shared/entity-organization'
import { unitsEnumArray, unitsEnumArrayEnabledByDefault } from '../units'
import { taxExemptionReasonEnum } from '../shared/tax-exemption-enum'

export const entityDocumentSettingsSchema = z.object({
  prefix: z.string(),
  includeYear: z.boolean(),
  separator: z.enum(['/', '-', '|', '.', '']),
  digits: z.number(),
  quotePrefix: z.string(),
  quoteConfirmationPrefix: z.string(),
  orderConfirmationPrefix: z.string(),
  deliveryNotePrefix: z.string(),
  invoicePrefix: z.string(),
  creditNotePrefix: z.string(),
  purchasePrefix: z.string(),
})
type EntityDocumentSettings = z.infer<typeof entityDocumentSettingsSchema>

export const entityBillableHoursDataSchema = z.object({
  defaultHourlyRate: z.number().int().min(0),
  useHourlyRateByDefault: z.boolean().default(false),
  users: z
    .array(
      z.object({
        mwUserId: z.number().int().min(0),
        hourlyRate: z.number().int().min(0),
      }),
    )
    .default([]),
})
export type EntityBillableHoursData = z.infer<typeof entityBillableHoursDataSchema>

export const entityUnitsSettingsSchema = z.object({
  availableUnits: z.array(
    z.object({
      code: z.enum(unitsEnumArray),
      enabled: z.boolean(),
    }),
  ),
})
export type EntityUnitsSettings = z.infer<typeof entityUnitsSettingsSchema>

const defaultEntityUnitsSettings: EntityUnitsSettings = {
  availableUnits: unitsEnumArray.map((code) => ({
    code,
    enabled: unitsEnumArrayEnabledByDefault.includes(code),
  })),
}

export const csvExportColumnKeys = [
  'documentId',
  'documentType',
  'fulfillmentDate',
  'issueDate',
  'dueDate',
  'status',
  'entityName',
  'entityVatId',
  'entityTaxId',
  'counterpartName',
  'counterpartVatId',
  'lineItemTitle',
  'unit',
  'quantity',
  'price',
  'rate',
  'currency',
  'netAmount',
  'taxAmount',
  'grossAmount',
  'createdAt',
] as const

export const entityCsvColumnPreferencesSchema = z.array(
  z.object({
    key: z.enum(csvExportColumnKeys),
    visible: z.boolean(),
  }),
)
export type EntityCsvColumnPreferences = z.infer<typeof entityCsvColumnPreferencesSchema>

export const defaultCsvColumnPreferences: EntityCsvColumnPreferences = csvExportColumnKeys.map(
  (key) => ({ key, visible: true }),
)

/**
 * Drops stored keys that no longer exist (e.g. a column was renamed/removed) and adds any columns
 * introduced since the preference was saved, so users don't silently lose access to them.
 *
 * A new column goes right after its predecessor in the default order, wherever the user moved
 * that one, and first when it has none: a column that belongs next to the customer name should
 * not turn up at the far end of the file. The saved columns keep their order and visibility.
 */
export function reconcileCsvColumnPreferences(
  stored: EntityCsvColumnPreferences | null | undefined,
): EntityCsvColumnPreferences {
  if (!stored) return defaultCsvColumnPreferences

  const knownKeys = new Set<string>(csvExportColumnKeys)
  const reconciled = stored.filter((entry) => knownKeys.has(entry.key))

  // In default order, so each missing key's predecessor is already in place - stored or inserted.
  csvExportColumnKeys.forEach((key, index) => {
    if (reconciled.some((entry) => entry.key === key)) return
    const predecessor = csvExportColumnKeys[index - 1]
    const at = reconciled.findIndex((entry) => entry.key === predecessor) + 1
    reconciled.splice(at, 0, { key, visible: true })
  })

  return reconciled
}

/**
 * The two numbers a DATEV Belegpaket needs from the user, both of which their Steuerberater
 * issues: the Beraternummer identifies the tax advisor's DATEV installation, the Mandantennummer
 * identifies this company inside it.
 *
 * Kept in their own column rather than folded into `settings`, which is the document-numbering
 * blob and has nothing to do with accounting — the `csvColumnPreferences` precedent.
 *
 * UNVERIFIED-AGAINST-XSD: the exact permitted length range is defined by the DATEV XML schema,
 * which is not in the repo (see `src/datev/datev-xml.ts`). The validation below therefore only
 * enforces what is certain — digits, non-empty — with a deliberately generous upper bound, so a
 * legitimate number is never refused locally. A number DATEV rejects is caught at import; a
 * number we wrongly refuse blocks the export entirely, which is the worse failure of the two.
 */
export const DATEV_NUMBER_MAX_LENGTH = 9

const datevNumberSchema = z.string().trim().regex(/^\d+$/).min(1).max(DATEV_NUMBER_MAX_LENGTH)

export const entityDatevSettingsSchema = z.object({
  /** Beraternummer — the tax advisor's DATEV consultant number. */
  consultantNumber: datevNumberSchema,
  /** Mandantennummer — this company's client number within that consultant's installation. */
  clientNumber: datevNumberSchema,
})
export type EntityDatevSettings = z.infer<typeof entityDatevSettingsSchema>

/**
 * Beta capabilities this company has switched on, keyed by feature. Feature flags in this codebase
 * are evaluated in the browser only, so an opt-in the API can enforce has to live on the entity.
 *
 * A record rather than a column per feature: the next beta is one key here plus one entry in the
 * settings page's registry, with no migration. An absent key means off, which is what every
 * existing company gets from the column's `{}` default.
 */
export const entityFeaturesSchema = z.object({
  /** Lets a document name an addressee other than the job's customer. */
  recipientOverride: z.boolean().optional(),
  /** Lets an invoice carry a Leitweg-ID, the routing identifier public-sector clients require. */
  buyerReference: z.boolean().optional(),
  /** Lets part of an issued invoice be credited by a Rechnungskorrektur. */
  invoiceCorrections: z.boolean().optional(),
  /** Lets a quote or invoice position carry a negative price or quantity: a deduction or credit. */
  negativeAmounts: z.boolean().optional(),
  /**
   * Shows each catalog item's image when picking from the catalog: the job materials widget's
   * "Select Material" dialog and the "Select from Catalog" dialogs of documents and bundles.
   */
  catalogSearchImages: z.boolean().optional(),
  /**
   * Links GAEB import positions to catalogue items by the article number they carry. Not a beta
   * on the beta page but an import setting; it lives here because every user can read it.
   */
  gaebCatalogMatching: z.boolean().optional(),
})
export type EntityFeatures = z.infer<typeof entityFeaturesSchema>
export type EntityFeatureKey = keyof EntityFeatures

export type AllowedCountries =
  | 'AF'
  | 'AX'
  | 'AL'
  | 'DZ'
  | 'AS'
  | 'AD'
  | 'AO'
  | 'AI'
  | 'AQ'
  | 'AG'
  | 'AR'
  | 'AM'
  | 'AW'
  | 'AU'
  | 'AT'
  | 'AZ'
  | 'BS'
  | 'BH'
  | 'BD'
  | 'BB'
  | 'BY'
  | 'BE'
  | 'BZ'
  | 'BJ'
  | 'BM'
  | 'BT'
  | 'BO'
  | 'BA'
  | 'BW'
  | 'BV'
  | 'BR'
  | 'IO'
  | 'BN'
  | 'BG'
  | 'BF'
  | 'BI'
  | 'KH'
  | 'CM'
  | 'CA'
  | 'IC'
  | 'CV'
  | 'KY'
  | 'CF'
  | 'EA'
  | 'TD'
  | 'CL'
  | 'CN'
  | 'CX'
  | 'CC'
  | 'CO'
  | 'KM'
  | 'CG'
  | 'CD'
  | 'CK'
  | 'CR'
  | 'CI'
  | 'HR'
  | 'CU'
  | 'CY'
  | 'CZ'
  | 'DK'
  | 'DJ'
  | 'DM'
  | 'DO'
  | 'EC'
  | 'EG'
  | 'SV'
  | 'GQ'
  | 'ER'
  | 'EE'
  | 'SZ'
  | 'ET'
  | 'FK'
  | 'FO'
  | 'FJ'
  | 'FI'
  | 'FR'
  | 'GF'
  | 'PF'
  | 'TF'
  | 'GA'
  | 'GM'
  | 'GE'
  | 'DE'
  | 'GH'
  | 'GI'
  | 'GR'
  | 'GL'
  | 'GD'
  | 'GP'
  | 'GU'
  | 'GT'
  | 'GG'
  | 'GN'
  | 'GW'
  | 'GY'
  | 'HT'
  | 'HM'
  | 'VA'
  | 'HN'
  | 'HK'
  | 'HU'
  | 'IS'
  | 'IN'
  | 'ID'
  | 'IR'
  | 'IQ'
  | 'IE'
  | 'IM'
  | 'IL'
  | 'IT'
  | 'JM'
  | 'JP'
  | 'JE'
  | 'JO'
  | 'KZ'
  | 'KE'
  | 'KI'
  | 'KP'
  | 'KR'
  | 'KW'
  | 'KG'
  | 'LA'
  | 'LV'
  | 'LB'
  | 'LS'
  | 'LR'
  | 'LY'
  | 'LI'
  | 'LT'
  | 'LU'
  | 'MO'
  | 'MG'
  | 'MW'
  | 'MY'
  | 'MV'
  | 'ML'
  | 'MT'
  | 'MH'
  | 'MQ'
  | 'MR'
  | 'MU'
  | 'YT'
  | 'MX'
  | 'FM'
  | 'MD'
  | 'MC'
  | 'MN'
  | 'ME'
  | 'MS'
  | 'MA'
  | 'MZ'
  | 'MM'
  | 'NA'
  | 'NR'
  | 'NP'
  | 'NL'
  | 'AN'
  | 'NC'
  | 'NZ'
  | 'NI'
  | 'NE'
  | 'NG'
  | 'NU'
  | 'NF'
  | 'MP'
  | 'MK'
  | 'NO'
  | 'OM'
  | 'PK'
  | 'PW'
  | 'PS'
  | 'PA'
  | 'PG'
  | 'PY'
  | 'PE'
  | 'PH'
  | 'PN'
  | 'PL'
  | 'PT'
  | 'PR'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RU'
  | 'RW'
  | 'SH'
  | 'KN'
  | 'LC'
  | 'PM'
  | 'VC'
  | 'WS'
  | 'SM'
  | 'ST'
  | 'SA'
  | 'SN'
  | 'RS'
  | 'SC'
  | 'SL'
  | 'SG'
  | 'SK'
  | 'SI'
  | 'SB'
  | 'SO'
  | 'ZA'
  | 'SS'
  | 'GS'
  | 'ES'
  | 'LK'
  | 'SD'
  | 'SR'
  | 'SJ'
  | 'SE'
  | 'CH'
  | 'SY'
  | 'TW'
  | 'TJ'
  | 'TZ'
  | 'TH'
  | 'TL'
  | 'TG'
  | 'TK'
  | 'TO'
  | 'TT'
  | 'TN'
  | 'TR'
  | 'TM'
  | 'TC'
  | 'TV'
  | 'UG'
  | 'UA'
  | 'AE'
  | 'GB'
  | 'US'
  | 'UM'
  | 'UY'
  | 'UZ'
  | 'VU'
  | 'VE'
  | 'VN'
  | 'VG'
  | 'VI'
  | 'WF'
  | 'EH'
  | 'YE'
  | 'ZM'
  | 'ZW'
  | 'BL'
  | 'BQ'
  | 'CW'
  | 'MF'
  | 'SX'

/** Pairs `entitiesRelations.paymentTerms` with `paymentTermsRelations.entity` for Drizzle relational queries. */
export const paymentTermsByEntityRelationName = 'paymentTermsByEntityId' as const

export const entities = pgTable(
  'entities',
  {
    id,
    mwCompanyId: integer('mw_company_id').notNull(),
    mwCompanyUuid: text('mw_company_uuid'),
    settings: jsonb('settings').notNull().$type<EntityDocumentSettings>(),
    organization: jsonb('organization').notNull().$type<EntityOrganization>(),
    billableHoursData: jsonb('billable_hours_data').$type<EntityBillableHoursData | null>(),
    units: jsonb('units')
      .notNull()
      .$type<EntityUnitsSettings>()
      .default(defaultEntityUnitsSettings),
    csvColumnPreferences: jsonb(
      'csv_column_preferences',
    ).$type<EntityCsvColumnPreferences | null>(),
    /**
     * Beraternummer / Mandantennummer for the DATEV Belegpaket export. Null until the user asks
     * their Steuerberater for them; the export refuses rather than emitting a package DATEV
     * would reject.
     */
    datevSettings: jsonb('datev_settings').$type<EntityDatevSettings | null>(),
    /** Beta capabilities this company has switched on. See {@link entityFeaturesSchema}. */
    features: jsonb('features').notNull().$type<EntityFeatures>().default({}),
    automatedRemindersEnabled: boolean('automated_reminders_enabled').notNull().default(false),
    /**
     * Pre-selected onto every new document that carries a zero-rated position. A default, not a
     * lock: it is copied onto the document at creation rather than read through at render time,
     * so changing it never rewrites a document that has already been issued.
     *
     * Every company starts on `exempt_other`, which carries no wording of its own and therefore
     * states nothing until the user types a reason. That is deliberate: it keeps a zero-rated
     * document issuable exactly as it was before this field existed, instead of stopping work in
     * flight on the day the field ships. A company that knows its treatment — a Kleinunternehmer,
     * say — changes the default once in settings and every document is right from then on.
     */
    defaultTaxExemptionReason: taxExemptionReasonEnum('default_tax_exemption_reason').default(
      'exempt_other',
    ),
    /**
     * The exemption wording copied onto every new document alongside the reason, and editable
     * there. An Austrian or Swiss company has no German reason to pick, so this note is how it
     * states its exemption: seeded from its country (`suggestedTaxExemptionNoteFor`) and re-seeded
     * if the country changes while it still can. Null for a German company, which picks a reason.
     */
    defaultTaxExemptionNote: text('default_tax_exemption_note'),
    defaultBankAccountId: text('default_bank_account_id').notNull(),
    /**
     * Null for a company with no VAT ID, such as some sole proprietors or AT/CH companies without
     * a UID.
     */
    defaultVatId: text('default_vat_id'),
    defaultPaymentTermsId: text('default_payment_terms_id').notNull(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [index('idx_entities_company_id').on(table.mwCompanyId)]
  },
)
export type EntityInsert = typeof entities.$inferInsert
export type Entity = typeof entities.$inferSelect

export const entityUserRoleEnum = pgEnum('entity_user_role', [
  'admin',
  'manager',
  'contributor',
  'trusted_employee',
  'employee',
  'no_access',
])
export type InvoicingUserRole = (typeof entityUserRoleEnum.enumValues)[number]

export const mwRoleEnum = pgEnum('mw_user_role', [
  'admin',
  'organizer',
  'office_manager',
  'project_manager',
  'service_worker',
  'worker',
  'viewer',
])
export type MWRole = (typeof mwRoleEnum.enumValues)[number]

export const getInvoicingUserRoleFromMwRole = (mwRole: MWRole | string): InvoicingUserRole => {
  switch (mwRole) {
    case 'admin':
    case 'organizer':
    case 'office_manager':
      return 'admin'
    case 'project_manager':
      return 'manager'
    case 'service_worker':
    case 'worker':
    case 'viewer':
      return 'employee'
    default:
      return 'employee'
  }
}

export const getInvoiceRoleRank = (role: InvoicingUserRole): number => {
  switch (role) {
    case 'admin':
      return 1
    case 'manager':
      return 2
    case 'contributor':
      return 3
    case 'trusted_employee':
      return 4
    case 'employee':
      return 5
    case 'no_access':
      return 6
    default:
      return 6
  }
}

export const getMwRoleRank = (role: MWRole | string): number => {
  switch (role) {
    case 'admin':
    case 'organizer':
    case 'office_manager':
      return 1
    case 'project_manager':
      return 2
    case 'service_worker':
    case 'worker':
    case 'viewer':
      return 5
    default:
      return 5
  }
}

export const entityUsers = pgTable(
  'entity_users',
  {
    id,
    email: text('email').notNull(),
    mwUserId: integer('mw_user_id').notNull(),
    mwUserUuid: text('mw_user_uuid'),
    role: entityUserRoleEnum().notNull().default('no_access'),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    markedInactiveAt: timestamp('marked_inactive_at', { mode: 'date' }),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_users_email').on(table.email),
      index('idx_users_entity_id').on(table.entityId),
      unique('idx_users_mw_user_id').on(table.mwUserId),
    ]
  },
)
export type EntityUser = typeof entityUsers.$inferSelect
export type EntityUserInsert = typeof entityUsers.$inferInsert

export const entityUsersRelations = relations(entityUsers, ({ one }) => ({
  entity: one(entities, {
    fields: [entityUsers.entityId],
    references: [entities.id],
  }),
}))

export const vatCountryEnum = pgEnum('vat_country', ['DE', 'AT', 'CH'])
export const vatTypeEnum = pgEnum('vat_type', ['eu_vat'])
export const vat = pgTable(
  'vat',
  {
    id,
    type: vatTypeEnum('type').notNull(),
    value: text('value').notNull(),
    country: vatCountryEnum('country').notNull(),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [index('idx_vat_entity_id').on(table.entityId)]
  },
)

export const vatRelations = relations(vat, ({ one }) => ({
  entity: one(entities, {
    fields: [vat.entityId],
    references: [entities.id],
  }),
}))

export type Vat = typeof vat.$inferSelect

export const bankAccounts = pgTable(
  'bank_accounts',
  {
    id,
    iban: text('iban').notNull(),
    bic: text('bic').notNull(),
    bankName: text('bank_name').notNull(),
    displayName: text('display_name').notNull(),
    country: countryEnum('country').notNull(),
    currency: currencyEnum('currency').notNull(),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [index('idx_bank_accounts_entity_id').on(table.entityId)]
  },
)
export type BankAccount = typeof bankAccounts.$inferSelect
export type BankAccountInsert = typeof bankAccounts.$inferInsert

const bankAccountInsertSchema = createInsertSchema(bankAccounts)
export const bankAccountUserInputSchema = bankAccountInsertSchema.omit({ entityId: true })

export const bankAccountsRelations = relations(bankAccounts, ({ one }) => ({
  entity: one(entities, {
    fields: [bankAccounts.entityId],
    references: [entities.id],
  }),
}))

export const counterpartTypeEnum = pgEnum('counterpart_type', ['individual', 'organization'])
export type CounterpartType = (typeof counterpartTypeEnum.enumValues)[number]

export const counterparts = pgTable(
  'counterparts',
  {
    id,
    type: counterpartTypeEnum('type').notNull(),
    mwClientId: integer('mw_client_id').notNull(),
    data: jsonb('data').notNull().$type<{
      defaultAddressId: string
      name?: string
      firstName?: string
      lastName?: string
      email?: string
      city?: string
      country?: string
      line1?: string
      line2?: string | null
      postalCode?: string
      buyerReference?: string
      /**
       * The customer's VAT ID, typed in the editor and remembered here for the next document. The
       * host app has no such field, so refreshing this row from job data must carry it over.
       */
      vatId?: string
    }>(),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_counterparts_entity_id').on(table.entityId),
      index('idx_counterparts_mw_client_id').on(table.mwClientId),
    ]
  },
)
export type Counterpart = typeof counterparts.$inferSelect

export type PaymentTermDiscount = {
  /**
   * The discount percentage in minor units. E.g., 200 means 2%, 1050 means 10.5%.
   */
  discount: number
  /**
   * The amount of days after the invoice issue date.
   */
  number_of_days: number
}

export interface CreatePaymentTermsMoniteResponse {
  id: string
  description?: string
  name: string
  term_1?: PaymentTermDiscount
  term_2?: PaymentTermDiscount
  term_final: {
    number_of_days: number
  }
}

export const paymentTerms = pgTable(
  'payment_terms',
  {
    id,
    name: text('name').notNull(),
    config: jsonb('config').notNull().$type<CreatePaymentTermsMoniteResponse>(),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [index('idx_payment_terms_entity_id').on(table.entityId)]
  },
)
export type PaymentTerm = typeof paymentTerms.$inferSelect
export type PaymentTermInsert = typeof paymentTerms.$inferInsert

export const paymentTermsRelations = relations(paymentTerms, ({ one }) => ({
  entity: one(entities, {
    relationName: paymentTermsByEntityRelationName,
    fields: [paymentTerms.entityId],
    references: [entities.id],
  }),
}))

export const documentTemplatesSchema = z.object({
  introductionNote: z.string(),
  closingNote: z.string(),
  emailSubject: z.string(),
  emailBody: z.string(),
})
export type DocumentTemplate = z.infer<typeof documentTemplatesSchema>

export const EntityHistoryEventTypeArray = ['user_created', 'user_updated'] as const
export const entityHistoryEventTypeEnumBase = pgEnum('entity_history_event_type_base', [
  ...EntityHistoryEventTypeArray,
])
export const entityHistoryEventTypeEnum = z.enum(EntityHistoryEventTypeArray)
export type EntityHistoryEventType = z.infer<typeof entityHistoryEventTypeEnum>

export const entityHistoryEvents = pgTable(
  'entity_history_events',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    eventType: entityHistoryEventTypeEnumBase('event_type')
      .notNull()
      .$type<EntityHistoryEventType>(),
    eventData: jsonb('event_data').notNull().$type<{
      [key: string]: string
    }>(),
    entityUserId: text('entity_user_id'),
    createdAt,
  },
  (table) => {
    return [
      index('idx_entity_history_events_entity_id').on(table.entityId),
      index('idx_entity_history_events_entity_user_id').on(table.entityUserId),
      index('idx_entity_history_events_created_at').on(table.createdAt),
    ]
  },
)
